// frontend/src/components/common/TableTotalsFooter.tsx
import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AggResult } from '@/types'
import { AGG_SYMBOL, formatAggValue } from '@/utils/columnAggregations'

export interface TotalsFooterColumn {
  key: string
  decimals?: number
  align?: 'left' | 'right'
  /** Offset izquierdo si la columna está congelada. */
  stickyLeft?: number
  width?: number
}

/**
 * Una fila de pie: sus propios totales y su propia etiqueta.
 *
 * Existe porque hay tablas donde totalizar todo junto es incorrecto. En el informe
 * de producto terminado, el stock reservado de un granel está en litros o botellas
 * montadas y el de un terminado en unidades de perfume: no son la misma magnitud, y
 * sumarlas en la misma celda rompe el cuadre que el cliente usa para validar el
 * informe. Cada sección lleva su fila y no se cruzan.
 */
export interface TotalsFooterGroup {
  /** Etiqueta de la fila, p. ej. «FINISHED GOODS». */
  label: string
  results: Record<string, AggResult>
  rowCount: number
  /** Sustituye el «N filas» de la etiqueta. Ver `rowLabel`. */
  rowLabel?: string
  /**
   * Totales de un nivel cuyas columnas NO están en esta rejilla, ya formateados.
   *
   * Se pintan como una franja de «RÓTULO valor» en una celda que ocupa el ancho
   * entero, en vez de repartirse por columnas. Es para las tablas con el detalle
   * anidado en subtablas: ahí las columnas del lote y del componente viven dentro
   * de su subtabla, así que un total suyo no tiene ninguna columna de la maestra
   * bajo la que caer — y con `results` a secas salía una fila con el recuento y
   * ni una cifra. Llevando el nombre delante, la franja se explica sola.
   */
  inline?: { label: string; value: string }[]
}

interface Props {
  dark: boolean
  /** Columnas VISIBLES, en el mismo orden exacto que la cabecera. */
  columns: TotalsFooterColumn[]
  results: Record<string, AggResult>
  /** Nº de filas sobre las que se ha calculado (el conjunto filtrado completo). */
  rowCount: number
  /**
   * Varias filas de pie, una por grupo. Cuando se pasa, sustituye a la fila única
   * de `results`/`rowCount`, y cada grupo rotula la suya con su nombre.
   */
  groups?: TotalsFooterGroup[]
  /**
   * Bloque de columnas congeladas a la izquierda. La etiqueta ocupa exactamente
   * esas columnas y queda fija, igual que hace la banda de grupos de la tabla
   * de Secuencia. Sin esto la etiqueta se extiende hasta el primer total.
   */
  stickyLabel?: { span: number; width: number }
  /**
   * Sustituye el «N filas» de la etiqueta. Para cuando el total no está
   * calculado sobre filas sino sobre entidades: en la cobertura de materiales se
   * deduplica por artículo, y decir «657 filas» junto a un número que agrega 350
   * artículos sería engañoso.
   */
  rowLabel?: string
  className?: string
}

/**
 * Fila de pie con los totales de columna, pegada al borde inferior.
 *
 * Las columnas anteriores al primer total se colapsan en una única celda con
 * `colSpan`, que aloja la etiqueta «Σ N filas». Si ninguna columna visible
 * tiene total, no se renderiza nada.
 */
export function TableTotalsFooter({
  dark, columns, results, rowCount, groups, stickyLabel, rowLabel, className = '',
}: Props) {
  const { t } = useTranslation()

  // Una fila o varias: por dentro siempre se trabaja con una lista.
  const filas: TotalsFooterGroup[] = groups?.length
    ? groups
    : [{ label: '', results, rowCount, rowLabel }]

  // La primera columna con total se busca sobre TODAS las filas: un grupo puede no
  // tener valor en una columna que otro sí totaliza (los semielaborados no tienen
  // cantidad pedida) y el pie debe conservar la misma rejilla en las dos.
  const firstIdx = columns.findIndex(c => filas.some(f => f.results[c.key]))
  // Las filas de franja no ocupan columnas, así que no cuentan para `firstIdx`:
  // sin esto, elegir un nivel que sólo se pinta como franja dejaba el pie entero
  // sin renderizar. Y las filas de rejilla se descartan cuando no hay ninguna
  // columna con total, que es lo que antes anulaba el pie completo.
  const hayFranja = filas.some(f => f.inline)
  // Nada que pintar: ni una columna con total ni una franja. El `return` no puede
  // ir aquí porque los hooks de más abajo tienen que ejecutarse siempre, así que
  // se decide con una bandera y se sale al final.
  const sinNada = firstIdx === -1 && !hayFranja
  const visibles = firstIdx === -1 ? filas.filter(f => f.inline) : filas

  // ── Apilado de varias filas pegajosas ──────────────────────────────────────
  //
  // Todas las celdas del pie son `sticky bottom-0`. Con una fila eso las pega al
  // fondo, y con varias las pega TODAS al mismo sitio: se montan unas sobre
  // otras y sólo se ve la última. Se notaba poco con dos grupos —los del informe
  // de producto terminado— y se ve a la primera con cuatro.
  //
  // Así que cada fila se separa del fondo lo que midan las que van por debajo.
  // Se mide en lugar de suponer una altura: la etiqueta puede ocupar dos líneas,
  // y en pedidos de venta hay celdas con una línea por divisa.
  const tfootRef = useRef<HTMLTableSectionElement>(null)
  const [offsets, setOffsets] = useState<number[]>([])

  useLayoutEffect(() => {
    const el = tfootRef.current
    if (!el) return
    const altos = [...el.rows].map(r => r.getBoundingClientRect().height)
    const nuevos = altos.map((_, i) => altos.slice(i + 1).reduce((a, h) => a + h, 0))
    // Sin la comparación, cada medida provocaría un render y el render otra
    // medida. Con una sola fila el desplazamiento es 0 y no cambia nada.
    setOffsets(prev => (
      prev.length === nuevos.length && prev.every((v, i) => Math.abs(v - nuevos[i]) < 0.5)
        ? prev
        : nuevos
    ))
    // Se remide cuando cambia el NÚMERO de filas o de columnas. Un cambio de alto
    // sin cambio de recuento —una celda que pasa a dos líneas— no se remide: hoy
    // sólo puede ocurrir en tablas de una única fila de pie, donde no se apila nada.
  }, [visibles.length, columns.length])


  // Con columnas congeladas la etiqueta no puede extenderse más allá del bloque
  // fijo: una celda con colSpan y ancho forzado comprimiría las columnas que
  // quedan fuera de él.
  const labelSpan = stickyLabel ? Math.min(stickyLabel.span, Math.max(firstIdx, 0)) : Math.max(firstIdx, 0)

  if (sinNada) return null

  // Fondo sólido: si fuese translúcido, las filas se transparentarían por debajo
  // al hacer scroll.
  const cellBase = `sticky bottom-0 px-3 py-2 text-[12px] border-t ${
    dark
      ? 'bg-slate-800 border-white/10 text-slate-100'
      : 'bg-slate-50 border-slate-200 text-slate-800'
  }`
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'

  return (
    <tfoot className={className} ref={tfootRef}>
      {visibles.map((fila, fi) => {
      // Lo que esta fila tiene que levantarse para no pisar a las de debajo.
      const bottom = offsets[fi] ?? 0
      return (
      <tr key={fila.label || fi}>
        {fila.inline ? (
          /* Franja rotulada, para un nivel sin columnas en esta rejilla. Va
             `sticky left-0` como las bandas de sección: con la tabla desplazada
             en horizontal, el rótulo tiene que seguir a la vista. */
          <td
            colSpan={columns.length}
            className={`${cellBase} sticky left-0 z-20`}
            style={{ bottom }}
          >
            <span className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
              <span className="font-semibold whitespace-nowrap">
                <span className="text-sky-500">Σ</span>{' '}
                {fila.label}{' '}
                <span className={`font-normal ${mutedTxt}`}>
                  {fila.rowLabel ?? t('common.agg.rows', {
                    count: fila.rowCount,
                    formattedCount: fila.rowCount.toLocaleString('es-ES'),
                  })}
                </span>
              </span>
              {fila.inline.map(dato => (
                <span key={dato.label} className="whitespace-nowrap">
                  <span className={`mr-1.5 text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>
                    {dato.label}
                  </span>
                  <span className="font-semibold tabular-nums">{dato.value}</span>
                </span>
              ))}
            </span>
          </td>
        ) : (
        <>
        {labelSpan > 0 && (
          <td
            colSpan={labelSpan}
            className={`${cellBase} whitespace-nowrap font-semibold z-20`}
            style={stickyLabel
              ? { bottom, left: 0, width: stickyLabel.width, minWidth: stickyLabel.width }
              : { bottom }}
          >
            <span className="text-sky-500">Σ</span>{' '}
            {fila.label && <span className="mr-1">{fila.label}</span>}
            <span className={`font-normal ${mutedTxt}`}>
              {/* `count` decide el plural; el número se pasa ya formateado para
                  que lleve separador de miles como el resto de la aplicación. */}
              {fila.rowLabel ?? t('common.agg.rows', {
                count: fila.rowCount,
                formattedCount: fila.rowCount.toLocaleString('es-ES'),
              })}
            </span>
          </td>
        )}

        {/* Columnas sin total entre el bloque fijo y el primer total. Las que van
            congeladas conservan su anclaje: la etiqueta sólo cubre las de cabeza,
            así que una congelada que vaya detrás de una libre —el código de
            artículo en materia prima— tiene que fijarse ella misma o el pie se
            despega del cuerpo al desplazar. */}
        {columns.slice(labelSpan, firstIdx).map(col => (
          <td
            key={col.key}
            className={`${cellBase} ${col.stickyLeft != null ? 'z-20' : 'z-10'}`}
            style={col.stickyLeft != null
              ? { bottom, left: col.stickyLeft, width: col.width, minWidth: col.width }
              : { bottom }}
          />
        ))}

        {columns.slice(firstIdx).map(col => {
          const res = fila.results[col.key]
          const style = col.stickyLeft != null
            ? { bottom, left: col.stickyLeft, width: col.width, minWidth: col.width }
            : { bottom }
          const alignCls = col.align === 'left' ? 'text-left' : 'text-right tabular-nums'
          const z = col.stickyLeft != null ? 'z-20' : 'z-10'

          if (!res) {
            return <td key={col.key} className={`${cellBase} ${z}`} style={style} />
          }

          const prefix = res.fn === 'sum' ? '' : `${AGG_SYMBOL[res.fn]} `

          return (
            <td
              key={col.key}
              className={`${cellBase} ${alignCls} ${z} font-semibold whitespace-nowrap`}
              style={style}
            >
              {res.kind === 'number' ? (
                // Sin valores que agregar no hay total: se pinta un guion, no un
                // cero. Un cero afirmaría que la métrica vale cero, y aquí lo que
                // pasa es que no aplica — es la misma distinción que en las celdas.
                res.count === 0 ? (
                  <span className={mutedTxt}>—</span>
                ) : (
                <>
                  <span className={`font-normal ${mutedTxt}`}>{prefix}</span>
                  {formatAggValue(res.value, res.fn, col.decimals)}
                </>
                )
              ) : res.parts.length === 0 ? (
                <span className={mutedTxt}>—</span>
              ) : (
                // Una línea por divisa: sumar EUR con USD daría una cifra sin
                // significado, así que nunca se colapsan en un único número.
                <span className="flex flex-col items-end leading-tight">
                  {res.parts.map(p => (
                    <span key={p.currency}>
                      <span className={`font-normal ${mutedTxt}`}>{prefix}</span>
                      {formatAggValue(p.value, res.fn, col.decimals, true)}
                      {p.currency && (
                        <span className={`ml-1 text-[10px] font-normal ${mutedTxt}`}>{p.currency}</span>
                      )}
                    </span>
                  ))}
                </span>
              )}
            </td>
          )
        })}
        </>
        )}
      </tr>
      )
      })}
    </tfoot>
  )
}
